<template>
  <div class="org-courses">
    <div class="page-header">
      <div class="title">课程管理</div>
      <el-button type="primary" icon="el-icon-circle-plus-outline" @click="openCreateCourseDialog">创建课程</el-button>
    </div>

    <el-card class="list-card">
      <el-table :data="courses" v-loading="loading" style="width: 100%">
        <el-table-column prop="course_name" label="课程名称" min-width="180" />
        <el-table-column prop="term" label="学期" width="140" />
        <el-table-column prop="teacher_name" label="负责人" width="160" />
        <el-table-column prop="organization" label="机构" min-width="160" />
        <el-table-column prop="inviteCode" label="邀请码" width="160">
          <template slot-scope="scope">
            <span style="font-family: monospace;">{{ scope.row.inviteCode || '-' }}</span>
            <el-button v-if="scope.row.inviteCode" type="text" icon="el-icon-document-copy" @click="copyText(scope.row.inviteCode)">复制</el-button>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160">
          <template slot-scope="scope">
            <el-button type="text" @click="goCourse(scope.row)">进入</el-button>
            <el-button type="text" @click="editCourse(scope.row)">修改</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="!loading && (!courses || courses.length === 0)" class="empty">
        <div class="empty-title">暂无课程</div>
        <div class="empty-desc">你可以先创建一个课程并生成班级邀请码</div>
        <el-button type="primary" icon="el-icon-circle-plus-outline" @click="openCreateCourseDialog">创建课程</el-button>
      </div>
    </el-card>

    <el-dialog
      title="创建课程"
      :visible.sync="createDialogVisible"
      width="560px"
      :close-on-click-modal="false"
    >
      <el-form ref="createFormRef" :model="createForm" label-width="90px">
        <el-form-item v-if="accountUsername" label="账号">
          <el-input :value="accountUsername" disabled />
        </el-form-item>
        <el-form-item label="课程名称" prop="courseName" :rules="[{ required: true, message: '课程名称不能为空', trigger: 'blur' }]">
          <el-input v-model="createForm.courseName" placeholder="请输入课程名称" />
        </el-form-item>
        <el-form-item label="学期">
          <el-input v-model="createForm.term" placeholder="例如：2026春" />
        </el-form-item>
        <el-form-item label="课程封面">
          <el-input v-model="createForm.image" placeholder="可选：封面URL" />
        </el-form-item>
        <el-form-item label="课程简介">
          <el-input v-model="createForm.profile" type="textarea" :rows="3" placeholder="可选" />
        </el-form-item>
        <el-form-item label="课程目标">
          <el-input v-model="createForm.target" type="textarea" :rows="3" placeholder="可选" />
        </el-form-item>
        <el-form-item label="负责人" prop="teacherName" :rules="[{ required: true, message: '负责人不能为空', trigger: 'blur' }]">
          <el-input v-model="createForm.teacherName" placeholder="默认：机构账号" />
        </el-form-item>
        <el-divider />
        <el-form-item label="班级名称" prop="className" :rules="[{ required: true, message: '班级名称不能为空', trigger: 'blur' }]">
          <el-input v-model="createForm.className" placeholder="例如：2026春一期" />
        </el-form-item>
        <el-form-item label="班级描述">
          <el-input v-model="createForm.classDescription" placeholder="可选" />
        </el-form-item>
      </el-form>

      <div v-if="createdInfo && createdInfo.inviteCode" style="margin-top: 10px;">
        <el-alert type="success" :closable="false">
          <div slot="title">
            邀请码：<b>{{ createdInfo.inviteCode }}</b>
            <el-button type="text" icon="el-icon-document-copy" @click="copyInviteCode">复制</el-button>
          </div>
        </el-alert>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="submitCreateCourse">创建</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getOrgCourses, createCourseWithClass } from '@/api/organization'

export default {
  name: 'OrgCourses',
  data() {
    return {
      loading: false,
      courses: [],
      createDialogVisible: false,
      creating: false,
      createdInfo: null,
      accountUsername: '',
      createForm: {
        courseName: '',
        term: '',
        profile: '',
        target: '',
        image: '',
        teacherName: '',
        className: '',
        classDescription: ''
      }
    }
  },
  created() {
    this.initAccountUsername()
    this.fetchCourses()
  },
  methods: {
    initAccountUsername() {
      try {
        const raw = localStorage.getItem('userInfo')
        if (!raw) return
        const u = JSON.parse(raw)
        const data = (u && u.data) ? u.data : u
        const uname = (data && (data.userName || data.username || data.account)) || ''
        if (uname) this.accountUsername = uname
      } catch (e) {}
    },
    async fetchCourses() {
      this.loading = true
      try {
        const res = await getOrgCourses()
        if (res && res.code === 1) {
          const list = res.data || []
          this.courses = list
        } else {
          this.$message.error((res && res.msg) || '获取课程失败')
        }
      } catch (e) {
        console.error('获取课程失败:', e)
        this.$message.error('获取课程失败')
      } finally {
        this.loading = false
      }
    },

    goCourse(row) {
      if (!row || !row.id) return
      this.$router.push({ path: `/org/courses/${row.id}`, query: { tab: 'classes' } })
    },

    editCourse(row) {
      if (!row || !row.id) return
      this.$router.push({ path: `/org/courses/${row.id}`, query: { tab: 'info' } })
    },

    copyText(text) {
      if (!text) return
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制')
      }).catch(() => {
        this.$message.error('复制失败，请手动复制')
      })
    },

    openCreateCourseDialog() {
      this.createdInfo = null
      this.createForm = {
        courseName: '',
        term: '',
        profile: '',
        target: '',
        image: '',
        teacherName: this.accountUsername || '',
        className: '',
        classDescription: ''
      }
      this.createDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.createFormRef) this.$refs.createFormRef.clearValidate()
      })
    },

    async submitCreateCourse() {
      if (!this.$refs.createFormRef) return

      this.$refs.createFormRef.validate(async(valid) => {
        if (!valid) return
        this.creating = true
        try {
          const res = await createCourseWithClass(this.createForm)
          if (res.code === 1) {
            this.createdInfo = res.data || null
            if (this.createdInfo && this.createdInfo.inviteCode) {
              this.$message.success('创建成功，邀请码已生成')
            } else {
              this.$message.success('创建成功')
            }
            await this.fetchCourses()
          } else {
            this.$message.error(res.msg || '创建失败')
          }
        } catch (e) {
          console.error('创建课程失败:', e)
          this.$message.error('创建失败')
        } finally {
          this.creating = false
        }
      })
    },

    copyInviteCode() {
      if (!this.createdInfo || !this.createdInfo.inviteCode) return
      navigator.clipboard.writeText(this.createdInfo.inviteCode).then(() => {
        this.$message.success('邀请码已复制到剪贴板')
      }).catch(() => {
        this.$message.error('复制失败，请手动复制')
      })
    }
  }
}
</script>

<style scoped>
.org-courses {
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.list-card {
  min-height: 240px;
}

.empty {
  padding: 40px 0;
  text-align: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 13px;
  color: #909399;
  margin-bottom: 16px;
}
</style>
